import { useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';

import type { Position } from 'components/RteEditor/Plugins/ToolbarPlugin';

import styles from './TableCellContextMenu.module.css';

import { Icon } from 'components/Icon';
import { useTranslation } from 'components/RteEditor/hooks/useTranslation';

export type Actions =
  | 'insertRowAbove'
  | 'insertRowBelow'
  | 'deleteRow'
  | 'deleteTable'
  | 'insertColumnLeft'
  | 'insertColumnRight'
  | 'deleteColumn';
interface TableCellContextMenuProps {
  position: Position;
  parentScrollPosition: number;
  onAction: (action: Actions) => void;
}

const CONTEXT_MENU_OFFSET = 25;
const WHITE_SPACE = 10;
const BUTTON_SHIFT = 4;
const BUTTON_RESET = 25;

const measureElement = (srcElement: HTMLElement) => {
  const measureElement = srcElement.cloneNode(true) as HTMLElement;
  measureElement.style.height = 'auto';
  measureElement.style.left = '-3000px';
  document.body.appendChild(measureElement);
  const height = measureElement.getBoundingClientRect().height;
  document.body.removeChild(measureElement);
  return height;
};

export const TableCellContextMenu = ({ position, parentScrollPosition, onAction }: TableCellContextMenuProps) => {
  const t = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [localPosition, setLocalPosition] = useState<Position>({ xPos: 0, yPos: 0, height: 0 });
  const [menuHeight, setMenuHeight] = useState(0);
  const startScrollPosition = useRef(0);
  const menuRef = useRef<HTMLUListElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [editor] = useLexicalComposerContext();

  const handleAction = (action: Actions) => {
    onAction(action);
    setIsOpen(false);
  };

  const flipMenuIfNotInViewport = () => {
    const menuWidth = menuRef.current ? menuRef.current.getBoundingClientRect().width : 0;

    if (containerRef.current && menuRef.current && buttonRef.current) {
      if (localPosition.xPos < menuWidth && containerRef.current) {
        containerRef.current.style.left = `${menuWidth - localPosition.xPos + WHITE_SPACE}px`;
        buttonRef.current.style.right = `${menuWidth - localPosition.xPos + BUTTON_SHIFT + WHITE_SPACE}px`;
      } else {
        containerRef.current.style.left = '0px';
        buttonRef.current.style.right = `${BUTTON_SHIFT}px`;
      }

      if (!menuHeight) {
        setMenuHeight(measureElement(menuRef.current));
      }

      if (menuHeight > document.documentElement.clientHeight - localPosition.yPos) {
        containerRef.current.style.top = `-${menuHeight + localPosition.height}px`;
        buttonRef.current.style.top = `${menuHeight + BUTTON_SHIFT}px`;
        buttonRef.current.style.transform = 'rotate(180deg)';
      } else {
        containerRef.current.style.top = '0px';
        buttonRef.current.style.top = `-${BUTTON_RESET}px`;
        buttonRef.current.style.transform = 'none';
      }
    }
  };

  const setMenuPosition = () => {
    flipMenuIfNotInViewport();
    return `translate(${(localPosition?.xPos ?? 0) - (menuRef.current ? menuRef.current.getBoundingClientRect().width : 0)}px, ${localPosition.yPos - (parentScrollPosition - startScrollPosition.current)}px)`;
  };

  useEffect(() => {
    setLocalPosition(position);
    if (position) {
      startScrollPosition.current = parentScrollPosition;
    }
    setIsOpen(false);
    setIsHidden(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position]);

  useEffect(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const rootElement = editor.getRootElement();
      if (!rootElement) return;
      if (
        rect.top - rootElement.getBoundingClientRect().top <= 0 ||
        rect.top - rootElement.getBoundingClientRect().bottom + CONTEXT_MENU_OFFSET >= 0
      ) {
        setIsHidden(true);
      } else {
        setIsHidden(false);
      }
      flipMenuIfNotInViewport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentScrollPosition]);

  return (
    <div
      className={clsx(styles['context-menu__wrapper'], [isHidden && styles['context-menu__wrapper--hidden']])}
      style={{
        transform: `${setMenuPosition()}`,
      }}
      ref={containerRef}
    >
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={styles['context-menu__button']}
        ref={buttonRef}
      >
        <Icon name="CheveronDown" size={16} />
      </button>
      <ul ref={menuRef} className={clsx(styles['context-menu'], isOpen && styles['context-menu--open'])}>
        <li>
          <button
            type="button"
            onClick={() => handleAction('insertRowAbove')}
            className={styles['context-menu__item']}
            aria-label={t.aria.labels.tableInsertRowAbove}
          >
            {t.table.insertRowAbove}
          </button>
        </li>
        <li>
          <button
            type="button"
            onClick={() => handleAction('insertRowBelow')}
            className={styles['context-menu__item']}
            aria-label={t.aria.labels.tableInsertRowBelow}
          >
            {t.table.insertRowBelow}
          </button>
        </li>
        <li className={styles['context-menu__item-divider']}>
          <button
            type="button"
            onClick={() => handleAction('deleteRow')}
            className={styles['context-menu__item']}
            aria-label={t.aria.labels.tableDeleteRow}
          >
            {t.table.deleteRow}
          </button>
        </li>

        <li>
          <button
            type="button"
            onClick={() => handleAction('insertColumnLeft')}
            className={styles['context-menu__item']}
            aria-label={t.aria.labels.tableInsertColumnLeft}
          >
            {t.table.insertColumnLeft}
          </button>
        </li>
        <li>
          <button
            type="button"
            onClick={() => handleAction('insertColumnRight')}
            className={styles['context-menu__item']}
            aria-label={t.aria.labels.tableInsertColumnRight}
          >
            {t.table.insertColumnRight}
          </button>
        </li>
        <li className={styles['context-menu__item-divider']}>
          <button
            type="button"
            onClick={() => handleAction('deleteColumn')}
            className={styles['context-menu__item']}
            aria-label={t.aria.labels.tableDeleteColumn}
          >
            {t.table.deleteColumn}
          </button>
        </li>

        <li>
          <button
            type="button"
            onClick={() => handleAction('deleteTable')}
            className={styles['context-menu__item']}
            aria-label={t.aria.labels.tableDeleteTable}
          >
            {t.table.deleteTable}
          </button>
        </li>
      </ul>
    </div>
  );
};
